import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Mic, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  ChevronRight, Clock, Eye, Headphones, ArrowLeft, Search,
  Shuffle, Repeat, Heart, Share2, Download, List, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Podcasts = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [podcasts, setPodcasts] = useState([]);
  const [loadingPodcasts, setLoadingPodcasts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Player state
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/podcasts/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPodcasts = async (subcategoryId) => {
    setLoadingPodcasts(true);
    try {
      const res = await axios.get(`${API}/podcasts/list?subcategory_id=${subcategoryId}`, { withCredentials: true });
      setPodcasts(res.data);
    } catch (err) {
      console.error('Podcast load error:', err);
    } finally {
      setLoadingPodcasts(false);
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
    setPodcasts([]);
  };

  const handleSubcategoryClick = async (subcategory) => {
    setSelectedSubcategory(subcategory);
    await loadPodcasts(subcategory.id);
  };

  const handleBack = () => {
    if (selectedSubcategory) {
      setSelectedSubcategory(null);
      setPodcasts([]);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  // Audio Player Functions
  const playPodcast = async (podcast) => {
    if (currentPodcast?.id === podcast.id) {
      togglePlayPause();
      return;
    }
    
    setCurrentPodcast(podcast);
    setShowPlayer(true);
    setIsPlaying(true);
    
    // Increment view count
    try {
      await axios.get(`${API}/podcasts/${podcast.id}`, { withCredentials: true });
    } catch (err) {
      console.error('View count error:', err);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newVolume = Math.max(0, Math.min(1, percent));
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime; audioRef.current.currentTime = Math.min(audioRef.current.duration || 0, current + 15);
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime; audioRef.current.currentTime = Math.max(0, current - 15);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const playNext = () => {
    const currentIndex = podcasts.findIndex(p => p.id === currentPodcast?.id);
    if (currentIndex < podcasts.length - 1) {
      playPodcast(podcasts[currentIndex + 1]);
    }
  };

  const playPrevious = () => {
    const currentIndex = podcasts.findIndex(p => p.id === currentPodcast?.id);
    if (currentIndex > 0) {
      playPodcast(podcasts[currentIndex - 1]);
    }
  };

  // Filter podcasts by search
  const filteredPodcasts = podcasts.filter(p => 
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-black/30 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {(selectedCategory || selectedSubcategory) && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="text-white hover:bg-white/10"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Headphones className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Podcast'ler</h1>
                  <p className="text-sm text-purple-200">
                    {selectedSubcategory?.name || selectedCategory?.name || 'Tüm Kategoriler'}
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/10"
            >
              Ana Sayfa
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Categories View */}
        {!selectedCategory && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Podcast Kategorileri</h2>
              <p className="text-purple-200">Kariyer yolculuğunuzda size eşlik edecek içerikler</p>
            </div>
            
            {categories.length === 0 ? (
              <div className="text-center py-16">
                <Mic className="w-20 h-20 mx-auto mb-4 text-purple-400 opacity-50" />
                <p className="text-xl text-purple-200">Henüz podcast kategorisi eklenmemiş</p>
                <p className="text-purple-300 mt-2">Yakında yeni içerikler eklenecek!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category) => (
                  <Card 
                    key={category.id}
                    className="group cursor-pointer bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 overflow-hidden"
                    onClick={() => handleCategoryClick(category)}
                    data-testid={`category-${category.id}`}
                  >
                    <CardContent className="p-6">
                      <div 
                        className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: `${category.color}30` }}
                      >
                        {category.icon}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{category.name}</h3>
                      <p className="text-purple-200 text-sm mb-4 line-clamp-2">
                        {category.description || 'Bu kategorideki podcast\'leri keşfedin'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-purple-300 text-sm">
                          {category.subcategories?.length || 0} alt kategori
                        </span>
                        <ChevronRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subcategories View */}
        {selectedCategory && !selectedSubcategory && (
          <div className="space-y-6">
            <div 
              className="p-6 rounded-2xl mb-8"
              style={{ backgroundColor: `${selectedCategory.color}20` }}
            >
              <div className="flex items-center gap-4">
                <span className="text-5xl">{selectedCategory.icon}</span>
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedCategory.name}</h2>
                  <p className="text-purple-200">{selectedCategory.description}</p>
                </div>
              </div>
            </div>

            {selectedCategory.subcategories?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-purple-200">Bu kategoride henüz alt kategori yok</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {selectedCategory.subcategories?.map((subcat) => (
                  <Card 
                    key={subcat.id}
                    className="group cursor-pointer bg-white/5 border-white/10 hover:bg-white/10 transition-all"
                    onClick={() => handleSubcategoryClick(subcat)}
                    data-testid={`subcategory-${subcat.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-2xl">{subcat.icon}</span>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{subcat.name}</h3>
                            <p className="text-purple-300 text-sm">{subcat.podcast_count || 0} podcast</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Podcasts View */}
        {selectedSubcategory && (
          <div className="space-y-6">
            <div className="p-6 rounded-2xl bg-white/5 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-4xl">{selectedSubcategory.icon}</span>
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedSubcategory.name}</h2>
                  <p className="text-purple-200">{selectedSubcategory.description}</p>
                </div>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" />
                <input
                  type="text"
                  placeholder="Podcast ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:border-purple-400"
                  data-testid="podcast-search"
                />
              </div>
            </div>

            {loadingPodcasts ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : filteredPodcasts.length === 0 ? (
              <div className="text-center py-12">
                <Mic className="w-16 h-16 mx-auto mb-4 text-purple-400 opacity-50" />
                <p className="text-purple-200">Bu kategoride henüz podcast yok</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPodcasts.map((podcast, index) => (
                  <Card 
                    key={podcast.id}
                    className={`group cursor-pointer transition-all ${
                      currentPodcast?.id === podcast.id 
                        ? 'bg-purple-600/30 border-purple-400' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                    onClick={() => playPodcast(podcast)}
                    data-testid={`podcast-${podcast.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                            currentPodcast?.id === podcast.id && isPlaying
                              ? 'bg-purple-500'
                              : 'bg-gradient-to-br from-purple-500/50 to-pink-500/50'
                          }`}>
                            {currentPodcast?.id === podcast.id && isPlaying ? (
                              <Pause className="w-6 h-6 text-white" />
                            ) : (
                              <Play className="w-6 h-6 text-white ml-1" />
                            )}
                          </div>
                          <span className="absolute -top-1 -left-1 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-xs font-bold text-white">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{podcast.title}</h3>
                          <p className="text-purple-300 text-sm truncate">{podcast.description}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-purple-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {podcast.duration_text || '0:00'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {podcast.view_count || 0} dinlenme
                            </span>
                            {podcast.is_featured && (
                              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded-full">
                                Öne Çıkan
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Audio Element */}
      {currentPodcast && (
        <audio
          ref={audioRef}
          src={currentPodcast.audio_url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={playNext}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          autoPlay
        />
      )}

      {/* Bottom Player */}
      {showPlayer && currentPodcast && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black via-purple-950/95 to-purple-900/90 backdrop-blur-xl border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4">
            {/* Progress Bar */}
            <div 
              className="h-1 bg-white/10 cursor-pointer group"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-gradient-to-r from-purple-400 to-pink-400 relative"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>

            <div className="py-4 flex items-center justify-between gap-4">
              {/* Track Info */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-white truncate">{currentPodcast.title}</h4>
                  <p className="text-purple-300 text-sm truncate">
                    {selectedSubcategory?.name || 'Podcast'}
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={playPrevious}
                  className="text-white hover:bg-white/10"
                  disabled={podcasts.findIndex(p => p.id === currentPodcast.id) === 0}
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={skipBackward}
                  className="text-white hover:bg-white/10"
                >
                  <span className="text-xs font-bold">-15</span>
                </Button>
                <Button
                  size="icon"
                  onClick={togglePlayPause}
                  className="w-12 h-12 rounded-full bg-white text-purple-900 hover:bg-purple-100"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={skipForward}
                  className="text-white hover:bg-white/10"
                >
                  <span className="text-xs font-bold">+15</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={playNext}
                  className="text-white hover:bg-white/10"
                  disabled={podcasts.findIndex(p => p.id === currentPodcast.id) === podcasts.length - 1}
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>

              {/* Time & Volume */}
              <div className="flex items-center gap-4 flex-1 justify-end">
                <span className="text-purple-300 text-sm whitespace-nowrap">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                
                <div className="hidden sm:flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleMute}
                    className="text-white hover:bg-white/10"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                  <div 
                    className="w-20 h-1 bg-white/20 rounded-full cursor-pointer"
                    onClick={handleVolumeChange}
                  >
                    <div 
                      className="h-full bg-white rounded-full"
                      style={{ width: `${isMuted ? 0 : volume * 100}%` }}
                    />
                  </div>
                </div>

                {currentPodcast.transcript && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowTranscript(!showTranscript)}
                    className={`text-white hover:bg-white/10 ${showTranscript ? 'bg-white/20' : ''}`}
                  >
                    <List className="w-5 h-5" />
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setShowPlayer(false); setCurrentPodcast(null); if(audioRef.current) audioRef.current.pause(); }}
                  className="text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transcript Modal */}
      {showTranscript && currentPodcast?.transcript && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-purple-950 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Transkript</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowTranscript(false)}
                className="text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <p className="text-purple-200 whitespace-pre-wrap leading-relaxed">
                {currentPodcast.transcript}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Podcasts;
